import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  Typography,
  Paper,
  alpha
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

// Paleta profesional verde-azul
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  purple: '#6c5ce7',
  dark: '#2d3436'
};

const DIFFICULTY_LEVELS = [
  { value: 'basico', label: 'Básico', color: COLORS.success },
  { value: 'intermedio', label: 'Intermedio', color: COLORS.warning },
  { value: 'avanzado', label: 'Avanzado', color: COLORS.error }
];

const defaultQuestion = {
  question: '',
  options: ['', '', '', ''],
  correctAnswer: '',
  difficulty: 'basico',
  index: -1
};

const QuestionEditDialog = ({ 
  open, 
  onClose, 
  question = defaultQuestion,
  onSave 
}) => {
  const [editedQuestion, setEditedQuestion] = React.useState(question || defaultQuestion);

  React.useEffect(() => {
    setEditedQuestion(question || defaultQuestion);
  }, [question]);

  const handleSave = () => {
    if (!editedQuestion.question || 
        !editedQuestion.options.some(o => o) || 
        !editedQuestion.correctAnswer) {
      return;
    }
    onSave(editedQuestion);
    onClose();
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...editedQuestion.options];
    newOptions[index] = value;
    setEditedQuestion({
      ...editedQuestion,
      options: newOptions,
      correctAnswer: newOptions.includes(editedQuestion.correctAnswer) 
        ? editedQuestion.correctAnswer 
        : ''
    });
  };

  const isNew = editedQuestion.index === -1;
  const difficultyColor = DIFFICULTY_LEVELS.find(l => l.value === editedQuestion.difficulty)?.color || COLORS.success;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${isNew ? COLORS.gradient1 : COLORS.warning} 0%, ${isNew ? COLORS.gradient2 : COLORS.orange} 100%)`,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 2.5
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {isNew ? <AddIcon /> : <EditIcon />}
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {isNew ? 'Nueva Pregunta' : 'Editar Pregunta'}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            {isNew ? 'Añade una nueva pregunta al pool' : `Editando pregunta #${editedQuestion.index + 1}`}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mt: 2 }}>
          {/* Dificultad */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Dificultad</InputLabel>
            <Select
              value={editedQuestion.difficulty || 'basico'}
              onChange={(e) => setEditedQuestion({
                ...editedQuestion, 
                difficulty: e.target.value
              })}
              label="Dificultad"
              sx={{ borderRadius: 2 }}
            >
              {DIFFICULTY_LEVELS.map(level => (
                <MenuItem key={level.value} value={level.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box 
                      sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        bgcolor: level.color 
                      }} 
                    />
                    <Typography>{level.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Pregunta */}
          <TextField
            fullWidth
            label="Pregunta"
            value={editedQuestion.question || ''}
            onChange={(e) => setEditedQuestion({
              ...editedQuestion, 
              question: e.target.value
            })}
            placeholder="Escribe la pregunta aquí..."
            multiline
            rows={2}
            sx={{ 
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': { borderColor: COLORS.primary },
                '&.Mui-focused fieldset': { borderColor: COLORS.primary }
              }
            }}
          />

          {/* Opciones */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.dark, mb: 2 }}>
            Opciones de respuesta
          </Typography>
          <Grid container spacing={2}>
            {editedQuestion.options.map((option, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <TextField
                  fullWidth
                  label={`Opción ${index + 1}`}
                  value={option || ''}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: editedQuestion.correctAnswer === option && option 
                        ? alpha(COLORS.success, 0.08) 
                        : 'transparent',
                      '&:hover fieldset': { borderColor: COLORS.primary },
                      '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>

          {/* Respuesta correcta */}
          <FormControl fullWidth sx={{ mt: 3 }}>
            <InputLabel>Respuesta correcta</InputLabel>
            <Select
              value={editedQuestion.correctAnswer || ''}
              onChange={(e) => setEditedQuestion({
                ...editedQuestion, 
                correctAnswer: e.target.value
              })}
              label="Respuesta correcta"
              sx={{ borderRadius: 2 }}
            >
              {editedQuestion.options.filter(op => op).map((option, index) => (
                <MenuItem key={index} value={option}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: COLORS.success 
                      }} 
                    />
                    {option}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Preview */}
          {editedQuestion.question && editedQuestion.correctAnswer && (
            <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(difficultyColor, 0.08),
                border: `1px solid ${alpha(difficultyColor, 0.2)}`
              }}
            >
              <Typography variant="caption" sx={{ color: difficultyColor, fontWeight: 700 }}>
                PREVIEW
              </Typography>
              <Typography sx={{ fontWeight: 600, color: COLORS.dark, mt: 1 }}>
                {editedQuestion.question}
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.success, fontWeight: 600, mt: 1 }}>
                ✓ {editedQuestion.correctAnswer}
              </Typography>
            </Paper>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button 
          onClick={onClose}
          startIcon={<CancelIcon />}
          sx={{
            color: '#636e72',
            fontWeight: 600,
            borderRadius: 2
          }}
        >
          Cancelar
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={!editedQuestion.question || 
                    !editedQuestion.options.some(o => o) || 
                    !editedQuestion.correctAnswer}
          sx={{
            background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
            fontWeight: 700,
            borderRadius: 2,
            px: 3,
            '&.Mui-disabled': {
              background: '#e0e0e0',
              color: '#9e9e9e'
            }
          }}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuestionEditDialog;